import React, { useEffect, useMemo, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Heart, Sparkles, ArrowLeft } from "lucide-react";
import { ShareLinkCard } from "@/components/ShareLinkCard";

const API = `${BACKEND_URL}/api`;

/**
 * Public tip jar for a DJ. Works 24/7 — no login required. Payment splits
 * automatically via Stripe Connect: 90 % lands in the DJ's own bank, 10 %
 * platform fee.
 *
 * Perfect for the venue QR-code scenario: DJ shows the QR on a screen at
 * the party, crowd points their phone camera, taps a preset amount, pays
 * with Apple Pay / Google Pay / card in seconds.
 */
export default function TipPage() {
  const { handle } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [dj, setDj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(1000); // default guaranteed-song tier (recalibrated on DJ load)
  const [customCents, setCustomCents] = useState("");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorDetail, setErrorDetail] = useState("");
  const [mmPolling, setMmPolling] = useState(false);
  const paid = params.get("paid");
  const paidAmount = parseInt(params.get("amount") || "0", 10);
  const paidSongs = parseInt(params.get("songs") || "0", 10);
  const mmRef = params.get("mm_ref");
  const mmCancel = params.get("mm_cancel");

  // Poll for Pesapal callback result — Pesapal's callback lands on this
  // page with ?mm_ref=<reference>, but the IPN reconciliation is async so
  // we poll /pesapal/tip/status until it flips to paid or fails.
  const [mmResult, setMmResult] = useState(null);
  // Public tipper wall (top-5 this week, first names only — social proof)
  const [tippers, setTippers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/dj/${encodeURIComponent(handle)}/tippers?window=7d`)
      .then(r => { if (!cancelled) setTippers(r.data?.tippers || []); })
      .catch(() => { /* non-fatal — wall is enhancement */ });
    return () => { cancelled = true; };
  }, [handle]);
  useEffect(() => {
    if (!mmRef) return;
    let cancelled = false;
    setMmPolling(true);
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/pesapal/tip/status/${encodeURIComponent(mmRef)}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setMmResult(r.data); setMmPolling(false);
          toast.success("Tip received — thanks!");
          return;
        }
        if (r.data.payment_status === "failed") {
          setMmResult(r.data); setMmPolling(false);
          toast.error("Payment did not complete. Please try again.");
          return;
        }
      } catch (_) { /* keep polling */ }
      setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
  }, [mmRef]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/public/dj/${encodeURIComponent(handle)}`);
        if (!cancelled) {
          setDj(r.data);
          // Default preset selection = the first "guaranteed 1 song" tier
          // so the button lights up in the user's currency out of the gate.
          const tiers = r.data.tiers || [];
          const one = tiers.find(t => (t.songs || 0) === 1) || tiers[Math.floor(tiers.length / 2)];
          if (one) setSelected(one.amount ?? one.amount_cents ?? 1000);
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || "DJ not found");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [handle]);

  const finalCents = useMemo(() => {
    if (customCents) {
      const n = Math.round(parseFloat(customCents));  // native units, integer
      return Number.isFinite(n) ? n : 0;
    }
    return selected;
  }, [customCents, selected]);

  // Region-native tier ladder returned by /public/dj/{handle}. For Global
  // DJs this is USD cents; for Kenya/Uganda it's whole KES/UGX. The
  // frontend never converts — it just displays what the backend says.
  const tiers = dj?.tiers || [];
  const currency = dj?.currency || "USD";
  const isMobileMoney = dj && ["kenya", "uganda"].includes((dj.payout_region || "global"));

  // Format an amount as a native-currency label. USD cents divide by 100 +
  // dollar prefix; KES/UGX are whole units + currency-code prefix.
  const fmtAmount = (amt) => {
    if (!currency || currency === "USD") return `$${(amt / 100).toFixed(0)}`;
    return `${currency} ${amt.toLocaleString()}`;
  };
  const fmtAmountFull = (amt) => {
    if (!currency || currency === "USD") return `$${(amt / 100).toFixed(2)} USD`;
    return `${currency} ${amt.toLocaleString()}`;
  };

  // Song-request tier for the currently-selected amount (matches the tier
  // ladder returned by the backend).
  const includedSongs = useMemo(() => {
    if (!tiers.length) return 0;
    let best = 0;
    for (const t of tiers) {
      const amt = t.amount ?? t.amount_cents ?? 0;
      if (finalCents >= amt && (t.songs || 0) > best) best = t.songs;
    }
    return best;
  }, [finalCents, tiers]);

  // Fallback for older responses that only return preset_amounts_cents.
  const presetAmounts = dj?.preset_amounts || dj?.preset_amounts_cents || [];
  const minAmount = dj?.min_amount ?? dj?.min_cents ?? 100;

  const sendTip = async () => {
    if (!dj) return;
    if (finalCents < minAmount) {
      toast.error(`Minimum tip is ${fmtAmountFull(minAmount)}`);
      return;
    }
    if (isMobileMoney) {
      const digits = phone.replace(/[^0-9]/g, "");
      if (digits.length < 9) {
        toast.error("Enter your mobile-money phone number");
        return;
      }
    }
    setBusy(true);
    setErrorDetail("");
    try {
      const endpoint = isMobileMoney ? "/public/tip/mobile-money" : "/public/tip";
      const body = {
        handle: dj.handle,
        message: message.slice(0, 200),
        tipper_name: (name || "anonymous").slice(0, 60),
      };
      if (isMobileMoney) {
        body.amount = finalCents;   // whole KES/UGX
        body.phone = phone.trim();
      } else {
        body.amount_cents = finalCents;  // USD cents
      }
      const r = await axios.post(`${API}${endpoint}`, body);
      window.location.href = r.data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Could not start checkout";
      setErrorDetail(`[${err.response?.status || "network"}] ${detail}`);
      toast.error(detail, { duration: 15000 });
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center text-white" data-testid="tip-page-loading">
        <Loader2 className="w-6 h-6 animate-spin"/>
      </div>
    );
  }
  if (!dj) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center text-white" data-testid="tip-page-notfound">
        <div className="text-center space-y-3">
          <div className="font-display font-black text-3xl">DJ not found</div>
          <button onClick={() => navigate("/")} className="text-[#00F0FF] font-mono uppercase tracking-widest text-xs">← Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white px-6 py-10 flex items-start justify-center" data-testid="tip-page">
      <div className="w-full max-w-lg space-y-6">
        <button onClick={() => navigate("/")} className="text-xs font-mono uppercase tracking-widest text-[#94A3B8] hover:text-white flex items-center gap-1" data-testid="tip-back">
          <ArrowLeft className="w-3 h-3"/> Back to NeuralDeck
        </button>

        {paid === "1" && (
          <div className="p-4 rounded-2xl border border-[#10B981]/60 bg-[#10B981]/10 flex items-center gap-3" data-testid="tip-success-banner">
            <Sparkles className="w-5 h-5 text-[#10B981] flex-shrink-0"/>
            <div>
              <div className="font-black text-lg">Thank you! ✨</div>
              <div className="text-sm text-[#94A3B8]">
                {paidAmount ? `Your $${(paidAmount / 100).toFixed(2)} USD is on its way to ${dj.display_name}.` : `Your tip is on its way to ${dj.display_name}.`}
                {paidSongs > 0 && (
                  <> The DJ will queue up your <span className="text-[#00F0FF] font-bold">{paidSongs} song request{paidSongs > 1 ? "s" : ""}</span>.</>
                )}
              </div>
            </div>
          </div>
        )}

        {mmRef && !mmResult && mmPolling && (
          <div className="p-4 rounded-2xl border border-[#00F0FF]/60 bg-[#00F0FF]/10 flex items-center gap-3" data-testid="tip-mm-pending-banner">
            <Loader2 className="w-5 h-5 text-[#00F0FF] animate-spin flex-shrink-0"/>
            <div>
              <div className="font-black text-lg">Confirming your tip…</div>
              <div className="text-sm text-[#94A3B8]">
                Waiting for your mobile-money network to settle the payment. This can take up to a minute.
              </div>
            </div>
          </div>
        )}
        {mmResult?.payment_status === "paid" && (
          <div className="p-4 rounded-2xl border border-[#10B981]/60 bg-[#10B981]/10 flex items-center gap-3" data-testid="tip-mm-success-banner">
            <Sparkles className="w-5 h-5 text-[#10B981] flex-shrink-0"/>
            <div>
              <div className="font-black text-lg">Tip landed ✨</div>
              <div className="text-sm text-[#94A3B8]">
                {mmResult.local_amount && mmResult.local_currency
                  ? `${mmResult.local_currency} ${Math.round(mmResult.local_amount).toLocaleString()} is on its way to ${dj.display_name}.`
                  : `Your tip is on its way to ${dj.display_name}.`}
                {(mmResult.songs_included || 0) > 0 && (
                  <> The DJ will queue up your <span className="text-[#00F0FF] font-bold">{mmResult.songs_included} song request{mmResult.songs_included > 1 ? "s" : ""}</span>.</>
                )}
              </div>
            </div>
          </div>
        )}
        {mmCancel === "1" && (
          <div className="p-4 rounded-2xl border border-[#FFC107]/60 bg-[#FFC107]/10 text-sm" data-testid="tip-mm-cancel-banner">
            You cancelled the payment. No money was taken.
          </div>
        )}

        <div className="rounded-3xl border border-[#2A354B] bg-gradient-to-br from-[#121620] to-[#0B0E14] p-8 space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center font-display font-black text-2xl text-black" data-testid="tip-dj-avatar">
              {(dj.display_name || dj.handle).slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">// tip jar</div>
              <div className="font-display font-black text-3xl leading-tight" data-testid="tip-dj-name">{dj.display_name || dj.handle}</div>
              <div className="text-xs text-[#94A3B8] font-mono" data-testid="tip-dj-handle">@{dj.handle}</div>
            </div>
          </div>

          {!dj.tips_enabled ? (
            <div className="p-4 rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/5 text-sm" data-testid="tip-not-enabled">
              This DJ hasn't finished setting up payouts yet. Check back in a few minutes.
            </div>
          ) : (
            <>
              {tippers.length > 0 && (
                <div className="rounded-xl border border-[#FFC107]/30 bg-gradient-to-br from-[#FFC107]/10 to-[#FF2A6D]/5 p-4 space-y-3" data-testid="tipper-wall">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-mono uppercase tracking-widest text-[#FFC107] flex items-center gap-2">
                      <span className="text-[#FFC107]">★</span> Top tippers this week
                    </div>
                    <div className="text-[9px] font-mono text-[#94A3B8] uppercase tracking-widest">7 days · amounts hidden</div>
                  </div>
                  <div className="space-y-1.5">
                    {tippers.map((t, idx) => {
                      const medal = idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`;
                      return (
                        <div key={`${t.first_name}-${idx}`}
                             data-testid={`tipper-wall-row-${idx}`}
                             className="flex items-center justify-between gap-3 py-1.5 px-2 rounded hover:bg-[#FFC107]/5">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-base w-6 text-center flex-shrink-0">{medal}</span>
                            <span className={`font-black truncate ${t.is_anonymous ? "text-[#94A3B8] italic" : "text-white"}`}>
                              {t.first_name}
                            </span>
                          </div>
                          <div className="text-xs font-mono text-[#94A3B8] whitespace-nowrap">
                            {t.tip_count} tip{t.tip_count > 1 ? "s" : ""}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-[#64748B] font-mono italic pt-1 border-t border-[#FFC107]/10">
                    // Tip now to climb the leaderboard — a shout-out in {dj.display_name}'s next set.
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-[#94A3B8] mb-2 flex items-center justify-between">
                  <span>Pick a tier</span>
                  <span className="text-[#94A3B8]/70">all prices in {currency}</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {presetAmounts.map(c => {
                    const t = tiers.find(x => (x.amount ?? x.amount_cents) === c) || {};
                    const s = t.songs || 0;
                    const active = selected === c && !customCents;
                    return (
                      <button key={c}
                        data-testid={`tip-amount-${c}`}
                        onClick={() => { setSelected(c); setCustomCents(""); }}
                        className={`flex flex-col items-center gap-1 px-3 py-4 rounded-xl border font-black transition-all ${active
                          ? "bg-[#00F0FF] text-black border-[#00F0FF]"
                          : "border-[#2A354B] hover:border-[#00F0FF] hover:bg-[#00F0FF]/5"}`}>
                        <span className="text-xl leading-none">{fmtAmount(c)}</span>
                        <span className={`text-[10px] font-mono uppercase tracking-widest ${active ? "text-black/70" : "text-[#94A3B8]"}`}>
                          {s} song{s > 1 ? "s" : ""}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="text-xs font-mono uppercase tracking-widest text-[#94A3B8] whitespace-nowrap">Custom</div>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] font-mono text-sm">
                      {currency === "USD" ? "$" : currency}
                    </span>
                    <Input
                      data-testid="tip-custom-amount"
                      type="number" min="1" step="1"
                      value={customCents}
                      onChange={e => setCustomCents(e.target.value)}
                      placeholder={currency === "USD" ? "e.g. 30" : (currency === "KES" ? "e.g. 2000" : "e.g. 50000")}
                      className={`bg-[#0B0E14] border-[#2A354B] text-white ${currency === "USD" ? "pl-6" : "pl-14"}`}/>
                  </div>
                </div>
                {finalCents >= minAmount && (
                  <div className={`mt-2 p-2 rounded-lg border text-xs text-center ${includedSongs > 0
                      ? "border-[#00FF66]/40 bg-[#00FF66]/5"
                      : "border-[#FFC107]/30 bg-[#FFC107]/5"}`} data-testid="tip-tier-preview">
                    {includedSongs > 0 ? (
                      <><span className="font-black text-[#00FF66]">✓ Guaranteed</span> · Includes <span className="font-black text-[#00FF66]">{includedSongs} song request{includedSongs > 1 ? "s" : ""}</span> · DJ must honour</>
                    ) : (
                      <><span className="font-black text-[#FFC107]">DJ discretion</span> · Shout-out tip · DJ may play a request if the vibe fits</>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="text-xs font-mono uppercase tracking-widest text-[#94A3B8]">Your name (optional)</div>
                <Input data-testid="tip-name"
                  value={name} onChange={e => setName(e.target.value)}
                  placeholder="e.g. Sarah"
                  className="bg-[#0B0E14] border-[#2A354B] text-white"/>
              </div>
              {isMobileMoney && (
                <div className="space-y-2">
                  <div className="text-xs font-mono uppercase tracking-widest text-[#94A3B8]">
                    Your {dj.payout_region === "kenya" ? "M-Pesa" : (dj.mobile_money_provider === "airtel_money" ? "Airtel Money" : "MTN MoMo")} phone number
                  </div>
                  <Input data-testid="tip-mm-phone"
                    value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder={dj.payout_region === "kenya" ? "+254 7XX XXX XXX" : "+256 7XX XXX XXX"}
                    inputMode="tel"
                    className="bg-[#0B0E14] border-[#2A354B] text-white"/>
                  <div className="text-[10px] text-[#64748B] font-mono">
                    Pesapal will prompt you on this number to approve the payment.
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <div className="text-xs font-mono uppercase tracking-widest text-[#94A3B8]">
                  {includedSongs > 0 ? (
                    <>🎵 Your song request{includedSongs > 1 ? "s" : ""} <span className="text-[#00F0FF] normal-case">— tell the DJ what to play</span></>
                  ) : (
                    <>Shout-out (optional)</>
                  )}
                </div>
                <Input data-testid="tip-message"
                  value={message} onChange={e => setMessage(e.target.value)}
                  placeholder={includedSongs > 0
                    ? (includedSongs === 1
                        ? "e.g. Fisayo Soyombo — Baby Come Home"
                        : `List your ${includedSongs} songs, one per line`)
                    : "Play something dirty ✨"}
                  className="bg-[#0B0E14] border-[#2A354B] text-white"/>
              </div>

              <Button data-testid="tip-submit"
                onClick={sendTip}
                disabled={busy || finalCents < minAmount}
                className="w-full bg-[#FF2A6D] text-white hover:bg-[#FF5588] font-black uppercase tracking-widest py-6 text-base">
                {busy ? <Loader2 className="w-5 h-5 animate-spin"/> : (
                  <><Heart className="w-5 h-5 mr-2"/> Tip {fmtAmountFull(finalCents)}</>
                )}
              </Button>

              {errorDetail && (
                <div className="p-3 rounded-lg border border-[#EF4444]/60 bg-[#EF4444]/10 text-xs font-mono break-words select-all" data-testid="tip-error-detail">
                  <div className="text-[#EF4444] uppercase tracking-widest mb-1">// Checkout error — screenshot this</div>
                  <div className="text-[#F8FAFC]">{errorDetail}</div>
                </div>
              )}

              <div className="text-[11px] text-center text-[#64748B] font-mono">
                {isMobileMoney ? (
                  <>
                    Charged in <span className="text-[#00F0FF] font-black">{currency}</span> · Secure checkout by Pesapal<br/>
                    {dj.payout_region === "kenya" ? "M-Pesa" : (dj.mobile_money_provider === "airtel_money" ? "Airtel Money" : "MTN MoMo")} · Card also available<br/>
                    90 % goes to the DJ · 10 % supports NeuralDeck
                  </>
                ) : (
                  <>
                    All amounts in <span className="text-[#00F0FF] font-black">USD</span> · Secure checkout by Stripe<br/>
                    Apple Pay / Google Pay / card / debit · your bank auto-converts on statement<br/>
                    90 % goes directly to the DJ · 10 % supports NeuralDeck
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <ShareLinkCard title="Share this tip jar" />
      </div>
    </div>
  );
}
